import { of } from 'rxjs';
import type { ActionsUnion} from '@rxsv/core';
import { createReducer, fromActions, U } from '@rxsv/core';
import { catchError, switchMap, map } from 'rxjs/operators';

import type { AppError } from 'portlet/common/errors';

import type { UserInfo } from './models';
import type { EffectFactory } from '../effects';
import type { AppSelector } from '../selectors';

export interface State {
    user: UserInfo | null;
    loading: boolean;
    error: AppError | null;
}

const initialState: State = {
    user: null,
    loading: false,
    error: null,
};

export const Action = U.createUnion(
    U.caseOf('FETCH_USER_INFO_REQ')(),
    U.caseOf('FETCH_USER_INFO_SUCCESS')<UserInfo>(),
    U.caseOf('FETCH_USER_INFO_ERROR')<AppError>(),
);

export type Action = ActionsUnion<typeof Action>;
export const ErrorActions = [Action.FETCH_USER_INFO_ERROR];

export const reducer = createReducer(initialState)<Action>({
    FETCH_USER_INFO_REQ: state => ({ ...state, loading: true }),
    FETCH_USER_INFO_SUCCESS: (state, { payload: user }) => ({
        ...state,
        user,
        loading: false,
        error: null,
    }),
    FETCH_USER_INFO_ERROR: (state, { payload: error }) => ({ ...state, error, loading: false }),
});

export const effect: EffectFactory =
    ({ authService }) =>
    action$ =>
        action$.pipe(
            fromActions(Action.FETCH_USER_INFO_REQ),
            switchMap(() =>
                authService.getUserInfo().pipe(
                    map(Action.FETCH_USER_INFO_SUCCESS),
                    catchError(err => of(Action.FETCH_USER_INFO_ERROR(err))),
                ),
            ),
        );

export const userSelector: AppSelector<UserInfo | null> = ({ auth }) => auth.user;

export const Selectors = {
    userSelector,
};
